import { UsersApi } from 'typescript-axios'

import React from 'react'
import { Badge } from 'reactstrap'
import { firstLetter } from 'common'
import moment from 'moment'
import { useRouter } from 'next/router'
import Link from 'next/link'
import WithLocale from '../../../../hocs/WithLocale'
import { getAxiosParams, getListings } from '../../../../utils/client'
import { translate } from '../../../../translations/utils'
import Stars from '../../../../components/Stars'
import useScript from '../../../../hooks/UseScript'
import IncomingListings from '../../../../components/IncomingListings'
import useTranslation from '../../../../hooks/UseTranslation'

const USER_SERVICE = new UsersApi(getAxiosParams())

export async function getStaticProps({ params, locale, defaultLocale }) {
  const user = await USER_SERVICE.readUserByIdApiV1UsersUserIdOrSlugGet(
    params.slug
  ).then((res) => res.data)
  const listings = await getListings({
    dateStart: moment(),
    owner: user.id,
    limit: 200
  })

  return {
    props: {
      nav: {
        light: true,
        classes: 'shadow',
        color: 'white'
      },
      hideCookies: true,
      hideHeader: true,
      hideFooter: true,
      noPaddingTop: true,
      listings,
      user,
      title: translate(
        'user_detail_page_title',
        locale,
        defaultLocale,
        user?.name_display
      ),
      locale,
      defaultLocale
    },
    revalidate: 180
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

const Profile = ({ user, listings }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const [primaryColor, setPrimaryColor] = React.useState('#636aee')
  const [showPrice, setShowPrice] = React.useState(false)
  const [pageSize, setPageSize] = React.useState(6)
  const [hideLogo, setHideLogo] = React.useState(false)
  const classRef = React.useRef()
  useScript('/content/js/iframeResizer.contentWindow.min.js')
  React.useEffect(() => {
    if (router.isReady) {
      if (router.query.primaryColor) {
        setPrimaryColor(`#${router.query.primaryColor}`)
      }
      if (router.query.pageSize) {
        setPageSize(parseInt(router.query.pageSize))
      }
      if (router.query.hl) {
        setHideLogo(Boolean(parseInt(router.query.hl)))
      }
      if (router.query.showPrice) {
        setShowPrice(parseInt(router.query.showPrice) == 1)
      }
    }
  }, [router.isReady])
  const hasRating = user.n_reviews > 5 || user.rating > 3
  return (
    <div className='p-2 p-md-4 h-100' style={{ backgroundColor: 'white' }}>
      <div className='text-center mb-4 position-relative'>
        <h6>
          <img
            id='avatar'
            src={user.avatar}
            alt={user.name_display}
            className='d-block avatar avatar-lg mb-2'
          />
          <div className='d-flex align-items-center justify-content-center mb-1'>
            {hasRating && (
              <div className='mb-2'>
                <Stars stars={user.rating} size='sm' />
              </div>
            )}
          </div>
          <div>
            {user.activities.map((activity, i) => (
              <Badge
                className={`text-xs mb-2 ${
                  i == user.activities.length - 1 ? '' : 'mr-2'
                }`}
                style={{ backgroundColor: primaryColor }}
                key={i}
              >
                {firstLetter(activity.name)}
              </Badge>
            ))}
          </div>
        </h6>
        <Link href='/users/[slug]' as={`/users/${user.slug}`}>
          <a className='tile-link' target='_blank' rel='noreferrer' />
        </Link>
      </div>
      <div ref={classRef}>
        {listings && listings.length > 0 ? (
          <IncomingListings
            showPrice={showPrice}
            openInNewTab
            pageSize={pageSize}
            listings={listings}
            onPageChange={() => null}
          />
        ) : (
          <p className='text-muted'>{t('user_detail_no_class')}</p>
        )}
      </div>
      {!hideLogo && (
        <div className='text-right mr-2'>
          <img
            src='https://gurubay.co/content/svg/logo.svg'
            alt='logo'
            style={{ width: '6rem' }}
            className='img-fluid mb-3'
          />
        </div>
      )}
    </div>
  )
}

const Page = (props) => <Profile {...props} />

export default WithLocale(Page)
